import React, { createContext, useCallback, useContext, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, X, ArrowRight, Crown, Sparkles } from "lucide-react";

const PaywallContext = createContext(null);

/**
 * Global paywall trigger. The axios interceptor in `lib/api.js` calls
 * `openPaywall({...})` whenever the backend responds with
 * `403 { code: "tier_required", ... }`. UI code can also call it
 * proactively for instant feedback — e.g. `if (user.tier === "free") openPaywall(...)`.
 */
export function PaywallProvider({ children }) {
    const [state, setState] = useState(null); // { required_tier, feature, message }

    const openPaywall = useCallback((payload) => {
        setState({
            required_tier: payload?.required_tier || "adventure",
            feature: payload?.feature || "use this feature",
            message: payload?.message || "",
        });
    }, []);

    const closePaywall = useCallback(() => setState(null), []);

    return (
        <PaywallContext.Provider value={{ openPaywall, closePaywall }}>
            {children}
            <PaywallModal state={state} onClose={closePaywall} />
        </PaywallContext.Provider>
    );
}

export function usePaywall() {
    const ctx = useContext(PaywallContext);
    if (!ctx) throw new Error("usePaywall must be used within PaywallProvider");
    return ctx;
}

const TIER_ORDER = { free: 0, adventure: 1, premium: 2 };

/**
 * React hook that returns a gate function. Caller passes { tier, feature, onAllowed }.
 * If the current user already meets the tier, `onAllowed()` runs immediately.
 * Otherwise, the paywall modal opens.
 */
export function useTierGate(user) {
    const { openPaywall } = usePaywall();
    return useCallback(
        ({ required_tier = "adventure", feature = "use this feature", onAllowed }) => {
            const userLevel = TIER_ORDER[user?.tier || "free"] ?? 0;
            const needed = TIER_ORDER[required_tier] ?? 1;
            // Admins always pass
            if (user?.role === "admin" || userLevel >= needed) {
                if (onAllowed) onAllowed();
                return true;
            }
            openPaywall({ required_tier, feature });
            return false;
        },
        [user, openPaywall]
    );
}

function PaywallModal({ state, onClose }) {
    if (!state) return null;
    const isPremium = state.required_tier === "premium";

    return (
        <div
            data-testid="paywall-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-midnight-pine/70 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                className="relative bg-snowcap rounded-lg shadow-2xl border border-ridge-line w-full max-w-md overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    data-testid="paywall-close"
                    onClick={onClose}
                    className="absolute top-3 right-3 text-slate-gray hover:text-granite p-1 z-10"
                    aria-label="Close"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="bg-gradient-to-br from-midnight-pine to-deep-evergreen text-white p-6 pt-8 text-center relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-ember-orange blur-2xl" />
                    </div>
                    <div className="relative">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-ember-orange/20 border border-ember-orange/50 mb-3">
                            {isPremium ? (
                                <Crown className="w-7 h-7 text-ember-orange" />
                            ) : (
                                <Sparkles className="w-7 h-7 text-ember-orange" />
                            )}
                        </div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-ember-orange mb-2">
                            <Lock className="w-3 h-3 inline-block -mt-0.5 mr-1" />
                            {isPremium ? "Premium feature" : "Adventure feature"}
                        </div>
                        <h3
                            data-testid="paywall-title"
                            className="font-display font-black text-2xl leading-tight"
                        >
                            Upgrade to {state.required_tier === "premium" ? "Premium" : "Adventure"}
                        </h3>
                        <p className="text-white/80 text-sm mt-2">
                            To {state.feature}, you'll need an active{" "}
                            <span className="font-bold text-ember-orange capitalize">
                                {state.required_tier}
                            </span>{" "}
                            subscription.
                        </p>
                    </div>
                </div>

                <div className="p-6">
                    <ul className="space-y-2.5 mb-5">
                        {(isPremium ? PREMIUM_PERKS : ADVENTURE_PERKS).map((perk) => (
                            <li key={perk} className="flex items-start gap-2 text-sm text-granite">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-ember-orange mt-2 shrink-0" />
                                <span>{perk}</span>
                            </li>
                        ))}
                    </ul>

                    <Link
                        to="/pricing"
                        data-testid="paywall-upgrade-cta"
                        onClick={onClose}
                        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-[0.98] transition-all"
                    >
                        See plans & upgrade
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                    <button
                        data-testid="paywall-maybe-later"
                        onClick={onClose}
                        className="w-full mt-2 px-5 py-2 text-xs font-bold text-slate-gray hover:text-granite transition-colors"
                    >
                        Maybe later
                    </button>
                </div>
            </div>
        </div>
    );
}

const ADVENTURE_PERKS = [
    "Post your own adventures with rich media + maps",
    "Comment and share trail notes with the community",
    "Collect Passport stamps and complete weekly challenges",
    "Start private conversations with other explorers",
    "Browse the full marketplace and contact sellers",
];

const PREMIUM_PERKS = [
    "Everything in Adventure tier",
    "List your own gear on the marketplace",
    "Priority placement in search and recommendations",
    "Ad-free experience across the entire platform",
];
